import React, { useState, useRef, useEffect, useContext, forwardRef, useImperativeHandle } from 'react'
import BBCodeParser from '../../libraries/BBCode/BBCodeParser'
import RPCodeParser from '../../libraries/RPCode/RPCodeParser'
import { gameActions } from '../../libraries/RPCode/GameActions'
import AppContext from '../../contexts/AppContext'
import './BBCodeEditor.css'

const BBCodeEditor = forwardRef(function BBCodeEditor(
  {
    value = '',
    onChange,
    placeholder = 'Escribe tu mensaje aquí...',
    showPreview = true,
    showToolbar = true,
    height = '300px',
    customTags = [],
    enableRPCode = true,
    onRPCodeExecuted,
    className = '',
    extraToolbarButtons = [],
    onSlashCommand,
  },
  ref
) {
  const { user, character } = useContext(AppContext)
  const [bbcodeText, setBbcodeText] = useState(value)
  const [showPreviewPane, setShowPreviewPane] = useState(false)
  const [executedCommands, setExecutedCommands] = useState([])
  const [rpCodeErrors, setRPCodeErrors] = useState([])

  const [parser] = useState(() => {
    const bbcodeParser = new BBCodeParser()

    // Registrar tags personalizados si se proporcionan
    customTags.forEach((tag) => {
      bbcodeParser.registerTag(tag.name, tag.config)
    })

    return bbcodeParser
  })

  const [rpCodeParser] = useState(() => new RPCodeParser(gameActions))

  const textareaRef = useRef(null)

  useEffect(() => {
    setBbcodeText(value)
  }, [value])

  const applyText = (newText, cursorStart, cursorEnd = cursorStart) => {
    setBbcodeText(newText)
    if (onChange) onChange(newText)
    setTimeout(() => {
      const ta = textareaRef.current
      if (!ta) return
      ta.focus()
      ta.setSelectionRange(cursorStart, cursorEnd)
    }, 0)
  }

  const insertAtCursor = (text, opts = {}) => {
    const textarea = textareaRef.current
    const start = textarea ? textarea.selectionStart : bbcodeText.length
    const end = textarea ? textarea.selectionEnd : start
    const newText = bbcodeText.substring(0, start) + text + bbcodeText.substring(end)
    const selectPlaceholder = opts.selectPlaceholder
    if (selectPlaceholder && text.includes(selectPlaceholder)) {
      const rel = text.indexOf(selectPlaceholder)
      applyText(newText, start + rel, start + rel + selectPlaceholder.length)
    } else {
      applyText(newText, start + text.length)
    }
  }

  useImperativeHandle(ref, () => ({
    insertAtCursor,
    getTextarea: () => textareaRef.current,
  }))

  const handleTextChange = async (e) => {
    const newValue = e.target.value
    const cursor = e.target.selectionStart
    setBbcodeText(newValue)

    // Slash UX: /jutsu (no se persiste — se borra al detectar)
    if (typeof onSlashCommand === 'function') {
      const before = newValue.slice(0, cursor)
      const slashMatch = before.match(/(?:^|\s)\/(jutsu)\s*$/i)
      if (slashMatch) {
        const cmd = slashMatch[1].toLowerCase()
        const removeFrom = before.lastIndexOf('/' + slashMatch[1])
        const cleaned = newValue.slice(0, removeFrom) + newValue.slice(cursor)
        setBbcodeText(cleaned)
        if (onChange) onChange(cleaned)
        setTimeout(() => {
          const ta = textareaRef.current
          if (ta) {
            ta.focus()
            ta.setSelectionRange(removeFrom, removeFrom)
          }
          onSlashCommand(cmd)
        }, 0)
        return
      }
    }

    // Siempre llamar onChange con el valor original que el usuario escribió
    if (onChange) {
      onChange(newValue)
    }

    // Procesar RPCode si está habilitado (solo para ejecutar comandos, no para modificar el texto)
    if (enableRPCode && rpCodeParser) {
      try {
        const context = {
          characterId: character?._id,
          adventureId: window.location.pathname.split('/').pop(),
          character: character,
          user: user,
        }

        const result = await rpCodeParser.process(newValue, context)

        // Actualizar comandos ejecutados
        if (result.executedCommands.length > 0) {
          setExecutedCommands((prev) => [...prev, ...result.executedCommands])
          if (onRPCodeExecuted) {
            onRPCodeExecuted(result.executedCommands)
          }
        }

        // Actualizar errores
        setRPCodeErrors(result.errors)

        // NO modificamos el texto del textarea, mantenemos lo que el usuario escribió
      } catch (error) {
        console.error('Error procesando RPCode:', error)
        setRPCodeErrors([error.message])
      }
    }
  }

  const insertTag = (openTag, closeTag = null, hasSelection = false) => {
    const textarea = textareaRef.current
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = bbcodeText.substring(start, end)

    let newText
    let newCursorPos

    if (closeTag) {
      // Tag con apertura y cierre
      if (selectedText) {
        newText = bbcodeText.substring(0, start) + openTag + selectedText + closeTag + bbcodeText.substring(end)
        newCursorPos = end + openTag.length + closeTag.length
      } else {
        newText = bbcodeText.substring(0, start) + openTag + closeTag + bbcodeText.substring(end)
        newCursorPos = start + openTag.length
      }
    } else {
      // Tag simple o con contenido
      newText = bbcodeText.substring(0, start) + openTag + bbcodeText.substring(end)
      newCursorPos = start + openTag.length
    }

    applyText(newText, newCursorPos)
  }

  const insertTagWithPrompt = (tagName, promptText, defaultValue = '') => {
    const value = prompt(promptText, defaultValue)
    if (value !== null) {
      insertTag(`[${tagName}=${value}]`, `[/${tagName}]`)
    }
  }

  const insertOocTag = () => {
    const note = prompt('Aclaración OOC (se verá al pasar el ratón):', '')
    if (note !== null && note.trim()) {
      insertTag(`[ooc=${note.trim()}]`, '[/ooc]')
    }
  }

  const toolbarButtons = [
    {
      title: 'Negrita',
      icon: 'B',
      className: 'bold',
      action: () => insertTag('[b]', '[/b]'),
    },
    {
      title: 'Cursiva',
      icon: 'I',
      className: 'italic',
      action: () => insertTag('[i]', '[/i]'),
    },
    {
      title: 'Subrayado',
      icon: 'U',
      className: 'underline',
      action: () => insertTag('[u]', '[/u]'),
    },
    {
      title: 'Tachado',
      icon: 'S',
      className: 'strikethrough',
      action: () => insertTag('[s]', '[/s]'),
    },
    { type: 'separator' },
    {
      title: 'Color',
      icon: '◈',
      action: () => insertTagWithPrompt('color', 'Ingresa el color (nombre, hex, rgb):', 'red'),
    },
    {
      title: 'Tamaño',
      icon: '◷',
      action: () => insertTagWithPrompt('size', 'Ingresa el tamaño (px, em, %):', '14px'),
    },
    { type: 'separator' },
    {
      title: 'Enlace',
      icon: '⧉',
      action: () => insertTagWithPrompt('url', 'Ingresa la URL:', 'https://'),
    },
    {
      title: 'Imagen',
      icon: '▣',
      action: () => insertTagWithPrompt('img', 'Ingresa la URL de la imagen:', 'https://'),
    },
    { type: 'separator' },
    {
      title: 'Cita',
      icon: '❝',
      action: () => insertTag('[quote]', '[/quote]'),
    },
    {
      title: 'Nota OOC inline',
      icon: '◎',
      action: insertOocTag,
    },
    {
      title: 'Código',
      icon: '◇',
      action: () => insertTag('[code]', '[/code]'),
    },
    {
      title: 'Spoiler',
      icon: '◉',
      action: () => insertTag('[spoiler]', '[/spoiler]'),
    },
    { type: 'separator' },
    {
      title: 'Lista',
      icon: '≡',
      action: () => insertTag('[list]\n[*]Elemento 1\n[*]Elemento 2\n[*]Elemento 3\n', '[/list]'),
    },
    {
      title: 'Centrar',
      icon: '◆',
      action: () => insertTag('[center]', '[/center]'),
    },
  ]

  const allToolbarButtons = [...toolbarButtons, ...extraToolbarButtons]

  const renderPreview = () => {
    const htmlContent = parser.parse(bbcodeText)
    return <div className="bbcode-preview" dangerouslySetInnerHTML={{ __html: htmlContent }} />
  }

  const charCount = bbcodeText.length

  return (
    <div className={`bbcode-editor ${className}`.trim()}>
      {/* Mostrar errores de RPCode */}
      {enableRPCode && rpCodeErrors.length > 0 && (
        <div className="rpcode-errors">
          <div className="rpcode-errors-title">Errores en comandos RPCode</div>
          <ul>
            {rpCodeErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Mostrar comandos ejecutados recientemente */}
      {enableRPCode && executedCommands.length > 0 && (
        <div className="rpcode-executed">
          <div className="rpcode-executed-title">Comandos ejecutados</div>
          <div className="executed-commands">
            {executedCommands.slice(-3).map((cmd, index) => (
              <div key={index} className="executed-command">
                <span className="command-name">[{cmd.command}]</span>
                <span className="command-result">{cmd.result.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showToolbar && (
        <div className="bbcode-toolbar">
          {allToolbarButtons.map((button, index) => {
            if (button.type === 'separator') {
              return <div key={index} className="toolbar-separator" />
            }

            return (
              <button
                key={index}
                type="button"
                className={`toolbar-button ${button.className || ''}`}
                title={button.title}
                onClick={button.action}
              >
                {button.icon}
              </button>
            )
          })}

          {showPreview && (
            <>
              <div className="toolbar-separator" />
              <button
                type="button"
                className={`toolbar-button ${showPreviewPane ? 'active' : ''}`}
                title="Vista previa"
                onClick={() => setShowPreviewPane(!showPreviewPane)}
              >
                ◈
              </button>
            </>
          )}
        </div>
      )}

      <div className="bbcode-editor-content" style={{ height }}>
        {showPreviewPane ? (
          <div className="bbcode-split-view">
            <div className="bbcode-input-pane">
              <textarea
                ref={textareaRef}
                value={bbcodeText}
                onChange={handleTextChange}
                placeholder={placeholder}
                className="bbcode-textarea"
              />
            </div>
            <div className="bbcode-preview-pane">{renderPreview()}</div>
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={bbcodeText}
            onChange={handleTextChange}
            placeholder={placeholder}
            className="bbcode-textarea"
            style={{ height: '100%' }}
          />
        )}
      </div>

      <div className="bbcode-editor__footer">
        <span className="bbcode-editor__count">{charCount} caracteres</span>
        <div className="bbcode-help">
        <details>
          <summary>Guía BBCode</summary>
          <div className="bbcode-help-content">
            <div className="help-section">
              <h4>Formato básico:</h4>
              <code>[b]negrita[/b]</code> - <strong>negrita</strong>
              <br />
              <code>[i]cursiva[/i]</code> - <em>cursiva</em>
              <br />
              <code>[u]subrayado[/u]</code> - <u>subrayado</u>
              <br />
              <code>[s]tachado[/s]</code> - <del>tachado</del>
            </div>

            <div className="help-section">
              <h4>Color y tamaño:</h4>
              <code>[color=red]texto rojo[/color]</code>
              <br />
              <code>[size=18px]texto grande[/size]</code>
            </div>

            <div className="help-section">
              <h4>Enlaces e imágenes:</h4>
              <code>[url=https://ejemplo.com]enlace[/url]</code>
              <br />
              <code>[img]https://ejemplo.com/imagen.jpg[/img]</code>
            </div>

            <div className="help-section">
              <h4>Otros:</h4>
              <code>[quote]cita[/quote]</code>
              <br />
              <code>[ooc=nota]texto con tooltip OOC[/ooc]</code>
              <br />
              <code>[code]código[/code]</code>
              <br />
              <code>[spoiler]contenido oculto[/spoiler]</code>
              <br />
              <code>[list][*]item 1[*]item 2[/list]</code>
            </div>
          </div>
        </details>
        </div>

      {/* Ayuda de RPCode */}
      {enableRPCode && (
        <div className="rpcode-help">
          <details>
            <summary>Comandos RPCode</summary>
            <div className="rpcode-help-content">
              <div className="rpcode-section">
                <h4>Navegación</h4>
                <code>[SALIR]</code> - Abandona la aventura
                <br />
                <code>[ENTRAR:adventureId]</code> - Únete a una aventura
              </div>

              <div className="rpcode-section">
                <h4>Combate</h4>
                <code>[ATACAR:targetId,weapon]</code> - Ataca a un objetivo
                <br />
                <code>[DEFENDER]</code> - Adopta posición defensiva
                <br />
                <code>[HUIR]</code> - Intenta huir del combate
              </div>

              <div className="rpcode-section">
                <h4>Magia</h4>
                <code>[HECHIZO:spellName,targetId]</code> - Lanza un hechizo
                <br />
                <code>[CURAR:targetId,amount]</code> - Cura a un objetivo
              </div>

              <div className="rpcode-section">
                <h4>Inventario</h4>
                <code>[USAR:itemId,targetId]</code> - Usa un objeto
                <br />
                <code>[EQUIPAR:itemId]</code> - Equipa un objeto
              </div>

              <div className="rpcode-section">
                <h4>Información</h4>
                <code>[STATS]</code> - Muestra estadísticas
                <br />
                <code>[INVENTARIO]</code> - Muestra inventario
                <br />
                <code>[DADO:sides,count]</code> - Lanza dados
                <br />
                <code>[TIRADA:skill,difficulty]</code> - Tirada de habilidad
              </div>

              <div className="rpcode-section">
                <h4>Social</h4>
                <code>[SUSURRAR:playerId,message]</code> - Mensaje privado
                <br />
                <code>[GRITAR:message]</code> - Mensaje público
              </div>
            </div>
          </details>
        </div>
      )}
      </div>
    </div>
  )
})

export default BBCodeEditor
